import React, { useState } from "react";
import invoke from "../../common/utils/invoke";
import "./TrackGrievanceStatusForm.scss";
import BrandLoader from "../../common/components/BrandLoader.jsx";
import Modal from "../../common/components/Modal.jsx";
import { logError } from "../../common/utils/logger.js";

const INITIAL_FORM_DATA = {
  srNumber: "",
};

const DEFAULT_HEADER_TITLE = "Track your grievance";

const TrackGrievanceStatusForm = ({ pageTitle }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [apiErrorMsg, setApiErrorMsg] = useState("");
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [srStatusData, setSrStatusData] = useState(null);

  const validateFormData = () => {
    const newErrors = {};

    // Validate SR number
    if (!formData.srNumber || !formData.srNumber.trim()) {
      newErrors.srNumber = "Enter SR number";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    return true;
  };

  const buildPayload = () => {
    return {
      srnumber: formData.srNumber.trim(),
    };
  };

  const handleApiResponse = (response) => {
    // Case 0: API failed / invoke returned null
    if (!response) {
      setApiErrorMsg("Unable to fetch SR status. Please try again later.");
      setShowErrorModal(true);
      setSrStatusData(null);
      return;
    }

    // Case 1: Check for result field (direct response format)
    if (response?.result) {
      setSrStatusData(response.result);
      setErrors({});
      return;
    }

    // Case 2: API returned success but with error message (operationResponse format)
    if (typeof response?.operationResponse === "string") {
      setApiErrorMsg(response.operationResponse);
      setSrStatusData(null);
      setShowErrorModal(true);
      return;
    }

    // Case 3: Valid data exists (operationResponse object format)
    if (response?.operationResponse) {
      setSrStatusData(response.operationResponse);
      setErrors({});
    } else {
      setApiErrorMsg("No data found. Please try again.");
      setShowErrorModal(true);
      setSrStatusData(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiErrorMsg("");

    if (!validateFormData()) {
      return;
    }

    const payload = buildPayload();
    setLoading(true);
    try {
      const response = await invoke.fetchSRStatus(payload);
      handleApiResponse(response);
    } catch (err) {
      logError(err);
      setFormData(INITIAL_FORM_DATA);
      setApiErrorMsg(
        err?.response?.data?.error ||
          "Invalid SR number. Please enter a valid SR number.",
      );
      setShowErrorModal(true);
      setSrStatusData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="track-grievance-wrapper">
      {loading && (
        <div className="loader-overlay">
          <BrandLoader color="#97262A" loading={loading} size={15} />
        </div>
      )}
      <div className="track-grievance">
        <div className="track-grievance__card">
          <div className="track-grievance__header">
            <h4 className="track-grievance__title">
              {pageTitle || DEFAULT_HEADER_TITLE}
            </h4>
          </div>
          <form className="track-grievance__form" onSubmit={handleSubmit}>
            <div className="form-row">
              <label className="label-one" htmlFor="srNumber">
                Enter reference number
              </label>
              <input
                id="srNumber"
                name="srNumber"
                type="text"
                value={formData.srNumber}
                onChange={handleChange}
                placeholder="Enter SR number"
                className={`form-input ${errors.srNumber ? "has-error" : ""}`}
                autoComplete="off"
              />
              {errors.srNumber && (
                <span className="error">{errors.srNumber}</span>
              )}
            </div>
            <div className="form-actions">
              <button
                className="track-button btn primary-button regular"
                type="submit"
              >
                Track
              </button>
            </div>
          </form>

          {srStatusData && (
            <div className="sr-status-result">
              <div className="sr-status-content">
                <div className="sr-status-header">
                  <h6 className="sr-status-title">Grievance Status</h6>
                </div>
                <div className="sr-status-text">
                  {typeof srStatusData === "string" 
                    ? srStatusData 
                    : JSON.stringify(srStatusData, null, 2)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Modal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setApiErrorMsg("");
        }}
        title="Something went wrong!"
        type="error"
        btnTitle="Try Again"
      >
        <p>{apiErrorMsg}</p>
      </Modal>
    </div>
  );
};

export default TrackGrievanceStatusForm;
